import React, { Component } from 'react';

import PropTypes from 'prop-types';

import { useLocale, pickKeys } from '@adsk/alloy-react-helpers';
import Overlay, { OverlayDeprecated } from '@adsk/alloy-react-overlay';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme from '@adsk/alloy-react-theme';

import {
  CREATE_TYPE,
  defaultItemRenderer,
  defaultFilterOptions,
  defaultMenuRenderer,
} from './defaultRenderers';

/**
 * SelectBoxControl
 */
class SelectBoxMenu extends Component {
  constructor(props) {
    super(props);

    this.renderItem = this.renderItem.bind(this);
    this.getOptionLabel = this.getOptionLabel.bind(this);

    const { multiple, selectedValue, stateAndHelpers } = props;
    this.prevInputValue = stateAndHelpers.inputValue;
    const selectedItems = multiple ? selectedValue : [];
    this.filteredSelectedItems = this.filterOptions(
      selectedItems,
      this.prevInputValue
    );
  }

  getOptionLabel(item) {
    const { labelGetter, labelKey } = this.props;
    return (labelGetter && labelGetter(item)) || item[labelKey] || '';
  }

  filterOptions(options, inputValue) {
    const { searchable, filterOptions = defaultFilterOptions } = this.props;

    if (!searchable) return options;
    return filterOptions(options, inputValue, this.props);
  }

  getOptions() {
    const {
      stateAndHelpers,
      multiple,
      creatable,
      selectedValue,
      isSameItem,
      options,
      labelKey,
      valueKey,
      showValueAsTags,
      keepSelectedOnOptionsChange,
    } = this.props;
    const { inputValue } = stateAndHelpers;
    const filteredOptions = this.filterOptions(options, inputValue);

    if (creatable && inputValue) {
      const search = inputValue.trim();
      if (
        !filteredOptions.find(
          (x) => this.getOptionLabel(x) === search || x[valueKey] === search
        )
      ) {
        const createOption = {
          [valueKey]: search,
          [labelKey]: search,
          [CREATE_TYPE]: true,
        };
        filteredOptions.unshift(createOption);
      }
    }

    if (showValueAsTags) return filteredOptions;
    if (multiple && inputValue !== this.prevInputValue) {
      this.prevInputValue = inputValue;
      this.filteredSelectedItems = this.filterOptions(
        multiple ? selectedValue : [],
        this.prevInputValue
      );
    }
    if (this.filteredSelectedItems.length === 0) return filteredOptions;

    const selectedOptions = [];
    const otherOptions = [];
    filteredOptions.forEach((item) => {
      if (this.filteredSelectedItems.find((x) => isSameItem(x, item)))
        selectedOptions.push(item);
      else otherOptions.push(item);
    });
    if (keepSelectedOnOptionsChange) {
      // in case some selected items are not in the current options
      this.filteredSelectedItems.length !== selectedOptions.length &&
        this.filteredSelectedItems.forEach((item) => {
          if (!selectedOptions.find((x) => isSameItem(x, item)))
            selectedOptions.push(item);
        });
    }
    return selectedOptions.concat(otherOptions);
  }

  renderContent() {
    const {
      className,
      style,
      stateAndHelpers,
      selectedValue,
      isLoading,
      maxMenuHeight,
      menuRenderer = defaultMenuRenderer,
      noResultsValue = this.props['T'].translate('NO_RESULTS'),
      noResultsPlaceholder = true,
    } = this.props;
    const { highlightedIndex } = stateAndHelpers;

    if (isLoading)
      return (
        <div
          css={[
            {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: maxMenuHeight,
            },
          ]}
        >
          <ProgressRing />
        </div>
      );

    const items = this.getOptions();
    if (items.length === 0) {
      return (
        <div
          css={[
            {
              padding: '8px 10px',
              color: theme.colors.charcoal500,
              display: noResultsPlaceholder ? 'block' : 'none',
            },
          ]}
        >
          {noResultsValue}
        </div>
      );
    }
    return (
      <ul
        className={className}
        css={[
          {
            borderRadius: 4,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            overflow: 'auto',
            maxHeight: maxMenuHeight,
          },
          style,
        ]}
      >
        {menuRenderer({
          items,
          renderItem: this.renderItem,
          selectedValue,
          highlightedIndex,
          selectProps: this.props,
        })}
      </ul>
    );
  }

  renderFooter() {
    const { stateAndHelpers, clearable, isSelected, footerRenderer } =
      this.props;
    const { clearSelection } = stateAndHelpers;

    const showClear = clearable && isSelected;
    const footer = footerRenderer && footerRenderer(this.props);
    if (!showClear && !footer) return null;

    return (
      <div
        css={[
          {
            borderTop: `1px solid ${theme.colors.charcoal200}`,
          },
        ]}
      >
        {footer}
        {showClear && (
          <button
            css={[
              {
                ...theme.typography.labelSmall,

                backgroundColor: theme.colors.white,
                border: 'none',
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                color: theme.colors.adskBlue700,
                cursor: 'pointer',
                outline: 'none',
                padding: '15px 16px',
                textAlign: 'right',
                width: '100%',

                ['&:not(:first-child)']: {
                  borderTop: '1px solid gray',
                },

                ['&[disabled]']: {
                  color: '#EDF0F2',
                  cursor: 'default',
                },
              },
            ]}
            onClick={clearSelection}
            type="button"
            data-testid="SelectBox__clear"
          >
            {this.props['T'].translate('CLEAR')}
          </button>
        )}
      </div>
    );
  }

  renderItem({ item, index, className, ...rest }) {
    const {
      stateAndHelpers,
      multiple,
      selectedValue,
      isSameItem,
      isOptionDisabled,
      showValueAsTags,
      valueKey,
      itemRenderer = defaultItemRenderer,
    } = this.props;
    const { getItemProps, highlightedIndex } = stateAndHelpers;

    const selected = multiple
      ? selectedValue.find((x) => isSameItem(x, item))
      : selectedValue && isSameItem(selectedValue, item);
    const lastSelected =
      multiple &&
      !showValueAsTags &&
      index === this.filteredSelectedItems.length - 1;
    const showSeparator = this.filteredSelectedItems.length > 0;
    const disabled =
      isOptionDisabled && isOptionDisabled(item, index, this.props);

    const itemProps = getItemProps({
      item,
      index,
      disabled,
      selected,
      key: item[valueKey],
      className,

      create: item[CREATE_TYPE] && !selected,
      highlighted: !disabled && index === highlightedIndex,
      lastSelected: lastSelected && showSeparator,

      ...rest,
    });

    return itemRenderer({
      item,
      index,
      disabled,
      selected,
      itemProps,
      selectProps: this.props,
    });
  }

  render() {
    const {
      stateAndHelpers,
      placement,
      container,
      target,
      overlayClassName,
      overlayStyle,
      selectedValue,
      multiple,
    } = this.props;

    const numSelected = multiple ? selectedValue.length : !!selectedValue;
    return (
      <OverlayDeprecated
        show
        target={target}
        container={container || null}
        placement={placement}
        layoutDependency={this.getOptions().length + numSelected}
      >
        {({ props, placement }) => (
          <Overlay.Container
            data-testid="SelectBox__menu"
            ref={props.ref}
            className={overlayClassName}
            placement={placement}
            variant={Overlay.VARIANTS.LIGHT}
            style={[
              {
                maxWidth: 'calc(100% + 70px)',
                width: target?.current?.offsetWidth || 'auto',
              },
              props.style,
              overlayStyle,
            ]}
          >
            <div {...stateAndHelpers.getMenuProps()}>
              {this.renderContent()}
              {this.renderFooter()}
            </div>
          </Overlay.Container>
        )}
      </OverlayDeprecated>
    );
  }
}

SelectBoxMenu.displayName = 'SelectBoxMenu';

SelectBoxMenu.defaultProps = {
  maxMenuHeight: 300,
};

SelectBoxMenu.propTypes = {
  ...pickKeys(Overlay.propTypes, ['container', 'placement']),

  /**
   * Class name for the component
   */
  className: PropTypes.string,
  /**
   * style for the component
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * i18n helper for T.translate('KEY')
   */
  T: PropTypes.object,
  /**
   * Whether the component is disabled
   */
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * Whether the component is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether the component is searchable
   */
  searchable: PropTypes.bool,
  /**
   * stateAndHelpers pass to control from select
   */
  stateAndHelpers: PropTypes.shape({
    isOpen: PropTypes.bool,
    openMenu: PropTypes.func,
    closeMenu: PropTypes.func,
    clearSelection: PropTypes.func,
    getMenuProps: PropTypes.func,
    getItemProps: PropTypes.func,
    type: PropTypes.string,
    inputValue: PropTypes.string,
    highlightedIndex: PropTypes.number,
  }),
  /**
   * `(inputValue, stateAndHelpers) => *`, callback when searching
   */
  onInputChange: PropTypes.func,
  /**
   * Whether the component is multi selectable
   */
  multiple: PropTypes.bool,
  /**
   * selectedValue
   */
  selectedValue: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  /**
   * The key of the option value
   */
  valueKey: PropTypes.string,
  /**
   * The placeholder for the component
   */
  placeholder: PropTypes.node,
  /**
   * The placeholder for the component when searching
   */
  searchPlaceholder: PropTypes.node,
  /**
   * Whether the item is Selected
   */
  isSelected: PropTypes.bool,
  /**
   * Container for the menu, the menu would be rendered on an overlay if set
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * Whether to show value as tags
   */
  showValueAsTags: PropTypes.bool,
  /**
   * The key of the option label
   */
  labelKey: PropTypes.string,
  /**
   * `(option) => string`, option label formatter
   */
  labelGetter: PropTypes.func,
  /**
   * `(options, inputValue, selectProps) => array`, filter options based on input value
   */
  filterOptions: PropTypes.func,
  /**
   * Whether the component is creatable
   */
  creatable: PropTypes.bool,
  /**
   * Whether the item is the same
   */
  isSameItem: PropTypes.func,
  /**
   * Controlled selected value for the component
   */
  options: PropTypes.arrayOf(PropTypes.object),
  /**
   * Class name for the overlay
   */
  overlayClassName: PropTypes.string,
  /**
   * A DOM Element, Ref to an element, or function that returns either. The `target` element is where
   * the overlay is positioned relative to.
   */
  target: PropTypes.any,
  /**
   * Class name for the menu
   */
  menuClassName: PropTypes.string,
  /**
   * `({ item, index, disabled, selected, itemProps, selectProps }) => node`, custom item renderer
   */
  itemRenderer: PropTypes.func,
  /**
   * `(option, index, selectProps) => boolean`, whether the option is disabled
   */
  isOptionDisabled: PropTypes.func,
  /**
   * Whether the component is loading options
   */
  isLoading: PropTypes.bool,
  /**
   * The max height of the menu, the footer is not included
   */
  maxMenuHeight: PropTypes.number,
  /**
   * `({ items, renderItem, selectedValue, highlightedIndex, selectProps }) => node`, custom menu renderer
   */
  menuRenderer: PropTypes.func,
  /**
   * The content to be shown if the filtered options is empty
   */
  noResultsValue: PropTypes.node,
  /**
   * Whether the component is clearable
   */
  clearable: PropTypes.bool,
  /**
   * `(selectProps) => node`, custom footer renderer
   */
  footerRenderer: PropTypes.func,
  /**
   * Specify where the SelectBoxMenu element is positioned
   * in relation to the target element
   */
  placement: PropTypes.oneOf(Object.values(Overlay.PLACEMENTS)),
};

const WrapperSelectBoxMenu = (props) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  return <SelectBoxMenu {...props} T={T} />;
};

export default WrapperSelectBoxMenu;
